import React from 'react';
import MobileHeader from './MobileHeader';
import MobilePageLayout from './MobilePageLayout';
import { Box, SxProps, Theme } from '@mui/material';
import { useCanSwitchCompany } from 'app/pages/mobile/hooks/useCanSwitchCompany';
import { useNativeStatusBarStyle } from 'app/pages/mobile/hooks/useNativeStatusBarStyle';
import {
  BACKGROUND_SIZES,
  MOBILE_HEADER_HEIGHT,
  MOBILE_HOME_SWITCHER_CONTENT_TOP,
  MOBILE_NAV_HEIGHT,
  NATIVE_SAFE_TOP,
  Z_LAYERS,
} from 'app/pages/mobile/utils/constants';

interface MobilePageContentProps {
  children: React.ReactNode;
  title?: string;
  centerTitle?: boolean;
  onBack?: () => void;
  transparentHeight?: string | number;
  isHome?: boolean;
  hideNavBar?: boolean;
  headerRight?: React.ReactNode;
  showCompanySwitcher?: boolean;
  sx?: SxProps<Theme>;
}

const getBackgroundHeight = (height?: string | number): string => {
  if (height === undefined) return BACKGROUND_SIZES.DEFAULT;
  if (typeof height === 'number') return `${height}vh`;
  return height;
};

// SEE-1715: the page now fills the screen, so 100vh includes the status bar.
// Navy heights were tuned as a share of the area below it — keep them that way.
const belowStatusBar = (height: string): string => {
  const vh = /^(\d+(?:\.\d+)?)vh$/.exec(height);
  return vh ? `calc((100vh - ${NATIVE_SAFE_TOP}) * ${Number(vh[1]) / 100})` : height;
};

const MobilePageContent = ({
  children,
  title,
  onBack,
  transparentHeight,
  sx,
  isHome,
  centerTitle,
  hideNavBar = false,
  headerRight,
  showCompanySwitcher = false,
}: MobilePageContentProps) => {
  // SEE-1715: the navy art runs up under the status bar (light icons); pages
  // with no navy area stay white there (dark icons). Header and content sit
  // below the bar either way.
  const navyHeight = getBackgroundHeight(transparentHeight);
  const hasNavyTop = !['0', '0vh'].includes(navyHeight);
  const backgroundHeight = hasNavyTop
    ? `calc(${belowStatusBar(navyHeight)} + ${NATIVE_SAFE_TOP})`
    : navyHeight;
  useNativeStatusBarStyle(hasNavyTop ? 'light' : 'dark');

  // Only Home shows the switcher line low enough to need a content offset, and
  // only when the line actually renders (same gate as MobileHeader: a switcher
  // page AND a user who can switch). Gating on canSwitch too avoids a dead gap
  // for single-company users, who see no line.
  const canSwitch = useCanSwitchCompany();
  const showsHomeSwitcherLine = isHome && showCompanySwitcher && canSwitch;
  const contentTop = showsHomeSwitcherLine
    ? MOBILE_HOME_SWITCHER_CONTENT_TOP
    : title
    ? MOBILE_HEADER_HEIGHT
    : '0px';

  return (
    <MobilePageLayout backgroundHeight={backgroundHeight}>
      {/* White Background Split */}
      <Box
        sx={{
          position: 'absolute',
          top: backgroundHeight,
          left: 0,
          right: 0,
          bottom: MOBILE_NAV_HEIGHT,
          background: '#FFFFFF',
          zIndex: Z_LAYERS.WHITE_BACKGROUND,
          overflow: 'hidden',
        }}
      />

      {/* Header */}
      <Box sx={{ position: 'fixed', top: NATIVE_SAFE_TOP, left: 0, right: 0 }}>
        <MobileHeader
          customTitle={!isHome ? title : undefined}
          showBackButton={!!onBack}
          onBackClick={onBack}
          showLogo={isHome}
          centerTitle={centerTitle}
          headerRight={headerRight}
          showCompanySwitcher={showCompanySwitcher}
        />
      </Box>

      {/* Scrollable Content - Floats above everything */}
      <Box
        sx={{
          position: 'fixed',
          top: `calc(${contentTop} + ${NATIVE_SAFE_TOP})`,
          left: 0,
          right: 0,
          bottom: hideNavBar ? 0 : MOBILE_NAV_HEIGHT,
          overflowX: 'hidden',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          zIndex: Z_LAYERS.CONTENT,
          px: 2,
          py: 3,

          ...sx,
        }}
      >
        {children}
      </Box>
    </MobilePageLayout>
  );
};

export default MobilePageContent;
